
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

const TestStripe = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handleCheckout = async () => {
    if (!user) {
      toast.error('Please log in to test the purchase');
      return;
    }

    setIsLoading(true);

    try {
      // Load Stripe.js dynamically
      const { loadStripe } = await import('@stripe/stripe-js');
      
      // Use your actual test publishable key
      const stripe = await loadStripe('pk_test_oGqDnG958Otcfg0iyVVyQ2rP');
      
      if (!stripe) {
        throw new Error('Failed to load Stripe');
      }

      console.log('Redirecting to Stripe Checkout with:', {
        priceId: 'price_1RSh64A0Bgm0r0TUE0EUpNVq',
        userId: user.id,
        userEmail: user.email
      });

      // Redirect to Stripe Checkout
      const { error } = await stripe.redirectToCheckout({
        lineItems: [
          {
            price: 'price_1RSh64A0Bgm0r0TUE0EUpNVq',
            quantity: 1,
          },
        ],
        mode: 'subscription',
        successUrl: `${window.location.origin}/test-stripe?success=true`,
        cancelUrl: `${window.location.origin}/test-stripe?canceled=true`,
        clientReferenceId: user.id, // This is the key field for the webhook
        customerEmail: user.email,
      });

      if (error) {
        console.error('Stripe redirect error:', error);
        toast.error(`Checkout error: ${error.message}`);
      }

    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error instanceof Error ? error.message : 'Checkout failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Check URL params for success/cancel status
  const urlParams = new URLSearchParams(window.location.search);
  const success = urlParams.get('success');
  const canceled = urlParams.get('canceled');

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>Stripe Membership Test</CardTitle>
          <CardDescription>
            Test the membership purchase flow with Stripe Checkout in test mode
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* User Info Display */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Current User Info:</h3>
            {user ? (
              <div className="space-y-1 text-sm">
                <p><strong>User ID:</strong> {user.id}</p>
                <p><strong>Email:</strong> {user.email}</p>
              </div>
            ) : (
              <p className="text-red-600">Not logged in</p>
            )}
          </div>

          {/* Success/Cancel Messages */}
          {success && (
            <Alert className="border-green-200 bg-green-50">
              <AlertDescription className="text-green-800">
                ✅ Subscription created successfully! The webhook should have processed this subscription.
                Check your Supabase logs and user_memberships table.
              </AlertDescription>
            </Alert>
          )}

          {canceled && (
            <Alert className="border-yellow-200 bg-yellow-50">
              <AlertDescription className="text-yellow-800">
                ⚠️ Subscription creation was canceled. No charges were made.
              </AlertDescription>
            </Alert>
          )}

          {/* Test Configuration */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Test Configuration:</h3>
            <div className="space-y-1 text-sm">
              <p><strong>Price ID:</strong> price_1RSh64A0Bgm0r0TUE0EUpNVq</p>
              <p><strong>Mode:</strong> Test Mode (recurring subscription)</p>
              <p><strong>Client Reference ID:</strong> {user?.id || 'Not set (user not logged in)'}</p>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Test Instructions:</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Make sure you're logged in (user ID shows above)</li>
              <li>Click "Test Subscription" to go to Stripe Checkout</li>
              <li>Use test card: <code className="bg-white px-1">4242 4242 4242 4242</code></li>
              <li>Use any future expiry date and any CVC</li>
              <li>Complete the subscription to trigger the webhook</li>
              <li>Check your Supabase logs to see webhook processing</li>
            </ol>
          </div>

          {/* Test Button */}
          <Button 
            onClick={handleCheckout}
            disabled={!user || isLoading}
            className="w-full"
            size="lg"
          >
            {isLoading ? 'Redirecting to Stripe...' : 'Test Subscription ($49.99/month)'}
          </Button>

          {!user && (
            <p className="text-red-600 text-center text-sm">
              Please log in to test the membership purchase flow
            </p>
          )}

          {/* Additional Info */}
          <div className="text-xs text-gray-500 space-y-1">
            <p><strong>Note:</strong> This uses Stripe's test environment. No real charges will be made.</p>
            <p><strong>Webhook URL:</strong> Your Supabase project should have the webhook configured to receive events from Stripe.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TestStripe;
